/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { IProcessedStyleSet } from '@fluentui/react/lib/Styling';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { InfoCardWithExternalLink, linkOption } from 'src/core/components/InfoCardWithExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { TelemetryFeatureTitle } from 'src/core/types/telemetryTypes';

type RuntimeCardsSectionProps = {
    sdkLink: string;
    pageName: string;
    projectId?: string;
    featureName: TelemetryFeatureTitle;
    githubSamplesLinkOptions: linkOption[];
    styles?: IProcessedStyleSet<IComponentStyles<IStackSlots>>;
};

const messages = defineMessages({
    sdkCardTitle: { id: 'modelDeploy.runtimeCardsSection.sdkCardTitle', defaultMessage: 'Use our SDK' },
    sdkCardLinkText: { id: 'modelDeploy.runtimeCardsSection.sdkCardLinkText', defaultMessage: 'Get SDK' },
    title: { id: 'modelDeploy.runtimeCardsSection.title', defaultMessage: 'Consume model after deployment' },
    githubSamplesCardTitle: { id: 'modelDeploy.runtimeCardsSection.githubSamplesCardTitle', defaultMessage: 'View samples on GitHub' },
    githubSamplesCardDropDownText: { id: 'modelDeploy.runtimeCardsSection.githubSamplesCardDropDownText', defaultMessage: 'SDK samples' },
    sdkCardDescription: {
        id: 'modelDeploy.runtimeCardsSection.sdkCardDescription',
        defaultMessage: 'Use our SDK to easily consume the model in runtime.'
    },
    githubSamplesCardDescription: {
        id: 'modelDeploy.runtimeCardsSection.githubSamplesCardDescription',
        defaultMessage: 'View samples on GitHub to understand how to consume use the SDKs.'
    }
});

export const RuntimeCardsSection = (props: RuntimeCardsSectionProps) => {
    const { sdkLink, styles, githubSamplesLinkOptions, featureName, pageName, projectId } = props;
    const { localize } = useLocalization();

    return (
        <Stack styles={styles} tokens={{ childrenGap: 20 }}>
            <LuisText as="h2" variant="large" fontWeight={500}>
                {localize(messages.title)}
            </LuisText>

            <Stack wrap horizontal tokens={{ childrenGap: 20 }}>
                <InfoCardWithExternalLink
                    linkHref={sdkLink}
                    linkKey="SDKLink"
                    pageName={pageName}
                    projectId={projectId}
                    featureName={featureName}
                    cardTitle={localize(messages.sdkCardTitle)}
                    linkText={localize(messages.sdkCardLinkText)}
                    cardDescription={localize(messages.sdkCardDescription)}
                />

                <InfoCardWithExternalLink
                    pageName={pageName}
                    projectId={projectId}
                    featureName={featureName}
                    linkOptions={githubSamplesLinkOptions}
                    cardTitle={localize(messages.githubSamplesCardTitle)}
                    cardDescription={localize(messages.githubSamplesCardDescription)}
                    optionsDropDownTitle={localize(messages.githubSamplesCardDropDownText)}
                />
            </Stack>
        </Stack>
    );
};
